/**
 * Horizon admin — application shell.
 *
 * docs/03 §10: Refine SPA on Vite, `@refinedev/supabase` for data, Ant Design
 * for the UI, Supabase auth gated on an `admin_users` row, and an access
 * control provider driven by the US-A01 role matrix.
 *
 * Authorisation is enforced twice — here so unusable controls are hidden, and
 * in RLS so a hidden control cannot be exercised through the API (US-A01 AC4).
 * Only the second one is security.
 */
import { ErrorComponent, useNotificationProvider } from '@refinedev/antd'
import '@refinedev/antd/dist/reset.css'
import { Authenticated, Refine } from '@refinedev/core'
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router'
import { dataProvider, liveProvider } from '@refinedev/supabase'
import { App as AntdApp, ConfigProvider } from 'antd'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AdminLocaleProvider } from '@/components/layout/AdminLocaleProvider'
import { supabaseClient } from '@/lib/supabase'
import { AdminUserListPage } from '@/pages/admin-users/list'
import { AuditLogPage } from '@/pages/audit-log/list'
import { CmsPagesEditorPage } from '@/pages/cms/pages'
import { FaqManagerPage } from '@/pages/cms/faq'
import { ContentItemCreatePage, ContentItemEditPage } from '@/pages/content-items/form'
import { ContentItemListPage } from '@/pages/content-items/list'
import { DashboardPage } from '@/pages/dashboard'
import { IngestMonitorPage } from '@/pages/ingest'
import { LoginPage } from '@/pages/login'
import { CommunityBrowserPage } from '@/pages/moderation/browser'
import { ModerationQueuePage } from '@/pages/moderation/list'
import { ReportShowPage } from '@/pages/moderation/show'
import { NotificationComposePage } from '@/pages/notifications/compose'
import { NotificationHistoryPage } from '@/pages/notifications/history'
import { PlanCreatePage, PlanEditPage } from '@/pages/plans/form'
import { PlanListPage } from '@/pages/plans/list'
import { ReferenceDataPage } from '@/pages/reference-data'
import { SubjectCreatePage, SubjectEditPage } from '@/pages/subjects/form'
import { SubjectListPage } from '@/pages/subjects/list'
import { SupportMessageListPage } from '@/pages/support-messages/list'
import { TransactionListPage } from '@/pages/transactions/list'
import { TransactionShowPage } from '@/pages/transactions/show'
import { UserListPage } from '@/pages/users/list'
import { UserShowPage } from '@/pages/users/show'
import { authProvider, getAdminRole } from '@/providers/auth-provider'
import { createAccessControlProvider } from '@/providers/access-control'
import { i18nProvider, t } from '@/providers/i18n-provider'
import { resources } from '@/resources'
import { adminTheme } from '@/theme'
import '@/styles/admin.css'

const accessControlProvider = createAccessControlProvider(getAdminRole)

export const App = () => (
  <BrowserRouter>
    <ConfigProvider theme={adminTheme}>
      <AntdApp>
        <Refine
          dataProvider={dataProvider(supabaseClient)}
          liveProvider={liveProvider(supabaseClient)}
          authProvider={authProvider}
          accessControlProvider={accessControlProvider}
          i18nProvider={i18nProvider}
          routerProvider={routerProvider}
          notificationProvider={useNotificationProvider}
          resources={resources}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            liveMode: 'off',
            projectId: 'horizon-admin',
          }}
        >
          <AdminLocaleProvider>
            <Routes>
              {/* Authenticated area — W-002 */}
              <Route
                element={
                  <Authenticated key="admin" fallback={<CatchAllNavigate to="/login" />}>
                    <AdminLayout>
                      <Outlet />
                    </AdminLayout>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />

                <Route path="/subjects">
                  <Route index element={<SubjectListPage />} />
                  <Route path="create" element={<SubjectCreatePage />} />
                  <Route path="edit/:id" element={<SubjectEditPage />} />
                </Route>

                <Route path="/content-items">
                  <Route index element={<ContentItemListPage />} />
                  <Route path="create" element={<ContentItemCreatePage />} />
                  <Route path="edit/:id" element={<ContentItemEditPage />} />
                </Route>

                <Route path="/ingest" element={<IngestMonitorPage />} />

                <Route path="/users">
                  <Route index element={<UserListPage />} />
                  <Route path=":id" element={<UserShowPage />} />
                </Route>

                <Route path="/moderation">
                  <Route index element={<ModerationQueuePage />} />
                  <Route path=":id" element={<ReportShowPage />} />
                </Route>
                <Route path="/community" element={<CommunityBrowserPage />} />

                <Route path="/plans">
                  <Route index element={<PlanListPage />} />
                  <Route path="create" element={<PlanCreatePage />} />
                  <Route path="edit/:id" element={<PlanEditPage />} />
                </Route>

                <Route path="/transactions">
                  <Route index element={<TransactionListPage />} />
                  <Route path="show/:id" element={<TransactionShowPage />} />
                </Route>

                <Route path="/notifications">
                  <Route path="compose" element={<NotificationComposePage />} />
                  <Route path="history" element={<NotificationHistoryPage />} />
                </Route>

                <Route path="/support" element={<SupportMessageListPage />} />

                <Route path="/cms">
                  <Route path="pages" element={<CmsPagesEditorPage />} />
                  <Route path="faq" element={<FaqManagerPage />} />
                </Route>

                <Route path="/reference-data" element={<ReferenceDataPage />} />
                <Route path="/admin-users" element={<AdminUserListPage />} />
                <Route path="/audit-log" element={<AuditLogPage />} />

                <Route path="*" element={<ErrorComponent />} />
              </Route>

              {/* W-001 — anonymous area */}
              <Route
                element={
                  <Authenticated key="anonymous" fallback={<Outlet />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
              </Route>
            </Routes>
          </AdminLocaleProvider>

          <UnsavedChangesNotifier />
          <DocumentTitleHandler
            handler={({ resource, action }) =>
              [
                resource?.name ? t(`admin.navigation.${resource.name}`) : undefined,
                action ? t(`actions.${action}`) : undefined,
                t('common.brand.name'),
              ]
                .filter(Boolean)
                .join(' · ')
            }
          />
        </Refine>
      </AntdApp>
    </ConfigProvider>
  </BrowserRouter>
)
